import type { CoreData } from './core';

/** One canonical trade row: [id, dayOff, acct, sym, side, lots, entry, exit, pnl, riskUnit, strat, tagged, time]. */
export type TradeTuple = CoreData['exTrades'][number];

/** Opening prices the reviewed-session rows are priced from, carried verbatim from the design reference `TRADES()`. */
const PX: Record<string, [number, number]> = {
  XAUUSD: [4036.59, 2],
  EURUSD: [1.1348, 5],
  GBPJPY: [198.42, 3],
  US30: [44120.0, 1],
  NAS100: [19240.0, 1],
  USDJPY: [163.22, 3],
  BTCUSD: [118240, 0],
};

/** The canonical trade list: the reviewed sessions from the journal days, priced into EX_TRADES-shaped rows, followed
 *  by EX_TRADES itself (design reference `TRADES()`).
 *
 *  This lives in contracts because two independent consumers must agree on it exactly: the web model renders it, and
 *  the Phase-2 seed writes it into MySQL as `trades` rows. If the derivation existed twice, the ledger on screen and
 *  the ledger in the database could drift by a rounding rule and nothing would catch it. */
export function canonicalTrades(core: CoreData): TradeTuple[] {
  const fmtPx = (v: number, dp: number) =>
    v.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  const rows: TradeTuple[] = [];
  let id = 100;
  core.revDays.forEach((D, off) => {
    D.entries.forEach((e) => {
      const [base, dp] = PX[e[0]] ?? [100, 2];
      const long = e[1] === 'LONG';
      const move = base * 0.0011 * Math.abs(e[2]);
      const exit = base + (long ? 1 : -1) * (e[3] >= 0 ? move : -move);
      rows.push([
        ++id,
        off,
        'Exness',
        e[0],
        long ? 'BUY' : 'SELL',
        0.1,
        fmtPx(base, dp),
        fmtPx(exit, dp),
        e[3],
        2400,
        e[4],
        1,
        e[5] === '—' ? '12:00' : e[5],
      ]);
    });
  });
  return rows.concat(core.exTrades);
}
